/**
 * EmailPreviewDialog — the email, as its recipient saw it (§ G37).
 *
 * Wade: *"When an organizer is added please ensure response shows 'email sent'
 * that's a link that does a popup to the actual email with its formatting as seen
 * by recipient."*
 *
 * ⚠️ Two decisions here are load-bearing, not stylistic:
 *
 *  1. **It renders `email_log.html_body` — the bytes that were actually sent.**
 *     Not a re-render from the current template. A re-render silently drifts as
 *     the theme and the copy change, and then this dialog is showing an email
 *     nobody ever received while looking like proof that they did.
 *
 *  2. **It renders inside an iframe with `sandbox=""` (every permission denied)
 *     and `srcDoc`** — never by injecting the markup into our own document. Email
 *     HTML has names, event titles and free-text notes interpolated into it; in
 *     our own document it would execute with our session. An empty `sandbox` gives
 *     the frame an opaque origin with no scripts, no forms and no top-level
 *     navigation — and it also stops the email's CSS bleeding into the app, which
 *     is why the preview looks like the email instead of a page fragment. The
 *     server strips active content too; this is the layer that does not depend on
 *     that having worked.
 */
import { useQuery } from '@tanstack/react-query';
import { Mail, AlertTriangle } from 'lucide-react';
import { emailApi } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

export default function EmailPreviewDialog({
  emailLogId,
  open,
  onOpenChange,
}: {
  emailLogId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['email-preview', emailLogId],
    queryFn: () => emailApi.preview(emailLogId as string),
    // Only fetch when the popup is actually open — a preview is a deliberate act.
    enabled: open && !!emailLogId,
    staleTime: Infinity,
    retry: false,
  });

  const sentAt = data?.sent_at ? new Date(data.sent_at) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-3xl overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4" />
            {data?.subject || 'The email we sent'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data ? (
              <>
                To <span className="font-medium text-foreground">{data.to_email}</span>
                {sentAt && <> · {sentAt.toLocaleString()}</>}
                {data.status !== 'sent' && (
                  <span className="ml-1 inline-flex items-center gap-1 text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    not delivered{data.error ? ` — ${data.error}` : ''}
                  </span>
                )}
              </>
            ) : (
              'Exactly what landed in their inbox.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-auto bg-muted/30">
          {isLoading && <p className="p-6 text-sm text-muted-foreground">Fetching the sent email…</p>}
          {isError && (
            <p className="p-6 text-sm text-destructive">
              {(error as any)?.response?.status === 404
                ? "That email isn't available to you."
                : 'Could not load the email.'}
            </p>
          )}
          {data && (
            <iframe
              // Every permission denied: no scripts, no forms, no navigation, and
              // an opaque origin. This is what makes rendering stored mail safe.
              sandbox=""
              srcDoc={data.html}
              title={`Email to ${data.to_email}`}
              className="h-[70vh] w-full border-0 bg-white"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
